import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { getAudioUrlByFilename, getMyAudio, uploadAudio } from "../../api/subjects";

export default function AudioPickerDialog({ open, onClose, onPick }) {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [audio, setAudio] = useState([]);
    const [desc, setDesc] = useState("");

    async function load() {
        setLoading(true);
        try {
            const data = await getMyAudio();
            setAudio(Array.isArray(data) ? data : []);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (open) load();
    }, [open]);

    const sorted = useMemo(() => {
        return [...audio].sort((a, b) =>
            String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || ""))
        );
    }, [audio]);

    async function onUploadFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const res = await uploadAudio(file, desc);
            const filename = res.filename;
            const url = getAudioUrlByFilename(filename);

            onPick?.({
                url,
                filename,
                originalFilename: res.originalFilename,
            });

            await load();
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    function pickFromList(item) {
        const filename = item.filename;
        const url = getAudioUrlByFilename(filename);

        onPick?.({
            url,
            filename,
            originalFilename: item.originalFilename,
            description: item.description,
        });
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Аудіо</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                    >
                        <TextField
                            label="Підпис / description (опційно)"
                            value={desc}
                            onChange={(e) => setDesc(e.target.value)}
                            fullWidth
                        />

                        <Button variant="contained" component="label" disabled={uploading}>
                            {uploading ? <CircularProgress size={18} /> : "Завантажити файл"}
                            <input hidden type="file" accept="audio/*" onChange={onUploadFile} />
                        </Button>
                    </Stack>

                    {loading ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography color="text.secondary">Завантаження...</Typography>
                        </Stack>
                    ) : sorted.length === 0 ? (
                        <Typography color="text.secondary">Ще немає аудіо</Typography>
                    ) : (
                        <Stack spacing={1.5} sx={{ maxHeight: 420, overflowY: "auto", pr: 1 }}>
                            {sorted.map((item) => {
                                const url = getAudioUrlByFilename(item.filename);

                                return (
                                    <Box
                                        key={item._id || item.id || item.filename}
                                        onClick={() => pickFromList(item)}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                            p: 1.5,
                                            cursor: "pointer",
                                            bgcolor: "background.paper",
                                            boxShadow: 1,
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: 3,
                                            },
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1}
                                                justifyContent="space-between"
                                                alignItems={{ sm: "center" }}
                                            >
                                                <Typography fontWeight={700}>
                                                    {item.originalFilename || item.filename}
                                                </Typography>
                                                {!!item.fileSize && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(Number(item.fileSize) / 1024).toFixed(0)} KB
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <audio
                                                controls
                                                src={url}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ width: "100%" }}
                                            >
                                                Your browser does not support the audio element.
                                            </audio>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Закрити</Button>
            </DialogActions>
        </Dialog>
    );
}
